/* Copyright (c) Trimble Inc. All rights reserved. */

import React from 'react';
import clsx from 'clsx';
import { Loader } from '@tcp-base/design-system';
import { UserAvatarModel } from './UserAvatar.model';
/**
 * UserAvatar View Component
 *
 * Pure presentation component that only renders JSX.
 * Contains no logic - all data and event handlers come from the
 */

/**
 * Avatar content render props
 */
interface AvatarContentProps {
  isLoading: boolean;
  imageLoading: boolean;
  shouldShowImage: boolean;
  imageUrl: string | null;
  shouldShowFallbackImage: boolean;
  fallbackImageUrl: string | null;
  handleImageError: () => void;
  handleImageLoad: () => void;
  displayName: string;
  loadingText: string;
  generatedInitials: string;
}

/**
 * Render the avatar content based on model state
 */
const renderAvatarContent = ({
  isLoading,
  imageLoading,
  shouldShowImage,
  imageUrl,
  shouldShowFallbackImage,
  fallbackImageUrl,
  handleImageError,
  handleImageLoad,
  displayName,
  generatedInitials,
}: AvatarContentProps): React.ReactElement => {
  // Show loading state for user data
  if (isLoading) {
    return (
      <div className='user-avatar__content user-avatar__loading'>
        <span className='user-avatar__text'>
          <Loader size='lg' color='primary' />
        </span>
      </div>
    );
  }

  // Show user picture if available and not errored
  if (shouldShowImage && imageUrl) {
    return (
      <>
        <img
          src={imageUrl}
          alt={`${displayName}`}
          className='user-avatar__image'
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: imageLoading ? 'none' : 'block' }}
        />
        {imageLoading && (
          <div className='user-avatar__content user-avatar__initials'>
            <span className='user-avatar__text'>{generatedInitials}</span>
            <div className='user-avatar__image-loader'>
              <Loader size='md' color='primary' />
            </div>
          </div>
        )}
      </>
    );
  }

  // Show fallback image if provided and user image failed
  if (shouldShowFallbackImage && fallbackImageUrl) {
    return (
      <img
        src={fallbackImageUrl}
        alt={`${displayName}`}
        className='user-avatar__image'
        onError={handleImageError}
      />
    );
  }

  // Fallback to initials
  return (
    <div className='user-avatar__content user-avatar__initials'>
      <span className='user-avatar__text'>{generatedInitials}</span>
    </div>
  );
};

/**
 * UserAvatar View Component
 *
 * Renders the complete UserAvatar UI based on the provided
 * Uses clsx for dynamic className generation.
 */
export const UserAvatarView: React.FC<UserAvatarModel> = ({
  displayName,
  generatedInitials,
  shouldShowImage,
  imageUrl,
  shouldShowFallbackImage,
  fallbackImageUrl,
  isLoading,
  imageLoading,
  hasError,
  handleImageError,
  handleImageLoad,
  handleClick,
  handleKeyDown,
  size,
  className,
  showName,
  namePosition,
  loadingText,
}) => {
  return (
    <div
      className={clsx(
        'user-avatar-container',
        showName && `user-avatar-container--with-name-${namePosition}`,
      )}
    >
      <div
        className={clsx(
          'user-avatar',
          `user-avatar--${size}`,
          {
            'user-avatar--clickable': handleClick,
            'user-avatar--error': hasError,
            'user-avatar--loading': isLoading || imageLoading,
          },
          className,
        )}
        onClick={handleClick}
        role={handleClick ? 'button' : undefined}
        tabIndex={handleClick ? 0 : undefined}
        onKeyDown={handleKeyDown}
        title={displayName}
      >
        {renderAvatarContent({
          isLoading,
          imageLoading,
          shouldShowImage,
          imageUrl,
          shouldShowFallbackImage,
          fallbackImageUrl,
          handleImageError,
          handleImageLoad,
          displayName,
          loadingText,
          generatedInitials,
        })}
      </div>

      {showName && (
        <div className='user-avatar__name'>
          <span className='user-avatar__name-text'>{isLoading ? loadingText : displayName}</span>
        </div>
      )}
    </div>
  );
};

export default UserAvatarView;
